import React, { useState, useEffect } from "react";
import profile from "../assets/images/user.png";
import background from "../assets/images/organizer_bg.jpeg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebookF,
  faInstagram,
  faTwitter,
} from "@fortawesome/free-brands-svg-icons";
import {
  faCalendarDays,
  faHourglass,
  faClock,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "../component/navbar/Navbar";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAttendeeStore, useStore } from "../utils/store";
import Footer from "../component/footer/Footer";
import upcomingEventDashboardApi from "../apis/dashboard/upcomingEvent.api";
import MetaData from "../component/Metadata";
import followApi from "../apis/follow.api";

const ProfilePage = () => {
  const { name, id } = useParams();
  const [loading, setLoading] = useState(true);
  const [organizer, setOrganizer] = useState(null);
  const [events, setEvents] = useState([]);
  const [movies, setMovies] = useState([]);
  const [month, setMonth] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [timeFilter, setTimeFilter] = useState("Upcoming");
  const [isFollowing, setIsFollowing] = useState(false);
  const { followers, setFollowers } = useStore();
  const role = useAttendeeStore((state) => state.user.role);
  const navigate = useNavigate();
  useEffect(() => {
    upcomingEventDashboardApi.getEventOrganizerById({
      id,
      success: (res) => {
        setOrganizer(res.data.organizerData);
        const createdDate = new Date(res.data.organizerData?.created_at);
        const monthYear = createdDate.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
        });
        setMonth(monthYear);
        setEvents(res.data.data.events || []);
        setMovies(res.data.data.movies || []);
        setLoading(false);
      },
      error: (err) => {
        console.error("Error fetching organizer data:", err);
        setLoading(false);
      },
    });
  }, [id]);

  function htmlToPlainText(html) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  }

  function formatDuration(duration) {
    if (duration === undefined || duration === null || isNaN(duration)) {
      return "N/A";
    }

    const hrs = Number(duration);

    return hrs === 1 ? `${hrs} hr` : `${hrs} hrs`;
  }

  function truncateDesc(desc, wordLimit) {
    const plainText = htmlToPlainText(desc);
    const words = plainText.split(" ");
    if (words.length > wordLimit) {
      return words.slice(0, wordLimit).join(" ") + "...";
    }
    return plainText;
  }

  const convertTo12HourFormat = (time24) => {
    const [hours, minutes, seconds] = time24.split(":").map(Number);

    const period = hours >= 12 ? "PM" : "AM";

    const hours12 = hours % 12 || 12;

    return `${String(hours12).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )} ${period}`;
  };

  function formatDate(dateString) {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const combinedList = [
    ...events.map((item) => ({ ...item, type: "event" })),
    ...movies.map((item) => ({ ...item, type: "movie" })),
  ];
  combinedList.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  let filteredList = [];
  if (activeTab === "All") {
    filteredList = combinedList;
  } else if (activeTab === "Events") {
    filteredList = combinedList.filter((item) => item.type === "event");
  } else if (activeTab === "Movies") {
    filteredList = combinedList.filter((item) => item.type === "movie");
  }

  if (timeFilter !== "All") {
    const now = new Date();
    if (timeFilter === "Upcoming") {
      filteredList = filteredList.filter(
        (item) => new Date(item.start_date) >= now
      );
    } else if (timeFilter === "Past") {
      filteredList = filteredList.filter(
        (item) => new Date(item.start_date) < now
      );
    }
  }

  useEffect(() => {
    if (followers) {
      const currentlyFollowing = followers.some(
        (follower) => follower.organizer_id === id
      );
      setIsFollowing(currentlyFollowing);
    }
  }, [id, followers]);

  const [animateFollow, setAnimateFollow] = useState(false);

  const toggleFollow = () => {
    setAnimateFollow(true);
    if (role !== "attendee") {
      alert("Please login as an attendee to follow the organizer.");
      navigate("/login");
      return;
    }

    if (isFollowing) {
      followApi.deleteFollower({
        id,
        success: (res) => {
          const updatedFollowers = followers.filter(
            (follower) => follower.organizer_id !== id
          );
          setFollowers(updatedFollowers);
          toast.success(
            "You will no longer receive updates about the organizer!",
            {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            }
          );
        },
        error: (err) => {
          console.error(err);
        },
      });
    } else {
      followApi.addFollower({
        payload: {
          organizer_id: id,
        },
        success: (res) => {
          const newFollower = { organizer_id: id };
          setFollowers([...followers, newFollower]);

          toast.success("You will receive updates about the organizer!", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        },
        error: (err) => {
          console.error(err);
        },
      });
    }

    setIsFollowing((prev) => !prev);
    setTimeout(() => setAnimateFollow(false), 300);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="animate-pulse h-[400px] bg-gray-200 rounded-lg"></div>
        <div className="py-10 px-4 space-y-6">
          <div className="flex flex-col md:flex-row md:items-start md:gap-10 mx-auto max-w-7xl">
            <div className="space-y-4">
              <div className="animate-pulse h-8 bg-gray-200 rounded-lg w-full"></div>
              <div className="animate-pulse h-6 bg-gray-200 rounded-lg w-48"></div>
            </div>
            <div className="w-full space-y-6">
              {[1, 2, 3].map((item) => (
                <div key={item} className="animate-pulse space-y-2">
                  <div className="h-48 bg-gray-200 rounded-lg"></div>
                  <div className="h-6 bg-gray-200 rounded-lg w-full"></div>
                  <div className="h-4 bg-gray-200 rounded-lg w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded-lg w-1/2"></div>
                </div>
              ))}
            </div>
            <div className="w-64 space-y-4">
              <div className="animate-pulse h-12 bg-gray-200 rounded-lg"></div>
              <div className="animate-pulse h-6 bg-gray-200 rounded-lg w-full"></div>
              <div className="animate-pulse h-6 bg-gray-200 rounded-lg w-3/4"></div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const renderCard = (item) => {
    const timeInfo = (
      <ul className="time-info absolute -top-5 left-0 right-0 w-[calc(100%-50px)] mx-auto flex items-center justify-between text-xs flex-wrap p-3 bg-white rounded-md shadow-md">
        <li className="flex items-center gap-1">
          <FontAwesomeIcon
            icon={faCalendarDays}
            className="text-primary text-sm"
          />
          <span>{formatDate(item.start_date)}</span>
        </li>
        <li className="flex items-center gap-1">
          <FontAwesomeIcon
            icon={faHourglass}
            className="text-primary font-light text-sm"
          />
          <span title="Duration" className="font-light">
            {item.type === "event"
              ? formatDuration(item.duration)
              : item.duration}
          </span>
        </li>
        <li className="flex items-center gap-1">
          <FontAwesomeIcon icon={faClock} className="text-primary text-sm" />
          <span className="font-light">
            {convertTo12HourFormat(item.start_time)}
          </span>
        </li>
      </ul>
    );

    return (
      <div
        key={item.type === "event" ? item.event_id : item.movie_id}
        className="event-item relative block mb-8 bg-white rounded-[6%] overflow-hidden shadow-lg"
      >
        <div className="event-image relative group">
          <Link
            to={
              item.type === "event"
                ? `/eventselection/${item.Event_Slug}/${item.event_id}`
                : `/movieselection/${item.movie_slug}/${item.movie_id}`
            }
          >
            <img
              src={item.thumbnail}
              alt={item.type === "event" ? item.Event_Name : item.movie_title}
              className="w-full h-48 object-cover object-center"
            />
          </Link>
          <div className="event-content relative p-5 shadow-sm">
            {timeInfo}
            <h5 className="mt-2 line-clamp-2 min-h-[50px] text-xl text-gray-950 font-semibold">
              <Link
                to={
                  item.type === "event"
                    ? `/eventselection/${item.Event_Slug}/${item.event_id}`
                    : `/movieselection/${item.movie_slug}/${item.movie_id}`
                }
              >
                {item.type === "event" ? item.Event_Name : item.movie_title}
              </Link>
            </h5>
            <p
              className="event-description font-light text-base text-[#0000007a] min-h-[60px] line-clamp-2 text-gray-400"
              dangerouslySetInnerHTML={{
                __html: truncateDesc(
                  item.type === "event"
                    ? item.description
                    : item.movie_description,
                  10
                ),
              }}
            />
            {item.type === "event" && (
              <div className="price-remain flex justify-between items-center text-sm mt-3 pt-3 border-t border-dashed border-gray-300">
                <div className="location text-primary flex items-center gap-2">
                  {item.event_type === "Venue" || item?.event_type === "Seats" ? (
                    <>
                      <FontAwesomeIcon icon={faLocationDot} />
                      <span className="line-clamp-1">
                        {item.City} - {item.country}
                      </span>
                    </>
                  ) : (
                    <p className="line-clamp-1">Online-Event</p>
                  )}
                </div>
              </div>
            )}
            {item.type === "movie" && (
              <div className="price-remain flex justify-between items-center text-sm mt-3 pt-3 border-t border-dashed border-gray-300">
                <div className="location text-primary flex items-center gap-2">
                  <FontAwesomeIcon icon={faLocationDot} />
                  <span className="line-clamp-1">{item.Theater}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  let displayList = [];
  if (activeTab === "All") {
    displayList = combinedList;
  } else if (activeTab === "Events") {
    displayList = events.map((item) => ({ ...item, type: "event" }));
  } else if (activeTab === "Movies") {
    displayList = movies.map((item) => ({ ...item, type: "movie" }));
  }

  return (
    <>
      <Navbar />
      <MetaData title={`${organizer?.organizer_name} | TicketVerz`} />
      <div
        className="relative bg-cover bg-center h-[400px] flex items-center justify-center"
        style={{ backgroundImage: `url('${background}')` }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        <div className="relative z-10 w-full px-8 max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between">
          <div className="flex items-center space-x-4 mb-4 md:mb-0">
            <img
              src={organizer?.organizer_photo || profile}
              alt="Profile Avatar"
              className="w-20 h-20 rounded-full"
            />
            <div>
              <h1 className="text-3xl font-semibold text-white">
                {organizer?.organizer_name}
              </h1>
              <p className="text-sm text-gray-200">Member since {month}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {organizer?.organizer_facebook && (
              <a
                href={organizer?.organizer_facebook}
                aria-label="Follow on Facebook"
                className="hover:text-blue-600 p-2 px-3 bg-white bg-opacity-60 rounded-full transition-colors duration-300 shadow-md"
              >
                <FontAwesomeIcon icon={faFacebookF} />
              </a>
            )}
            {organizer?.organizer_instagram && (
              <a
                href={organizer?.organizer_instagram}
                aria-label="Follow on Instagram"
                className="hover:text-pink-500 p-2 px-3 bg-white bg-opacity-60 rounded-full transition-colors duration-300 shadow-md"
              >
                <FontAwesomeIcon icon={faInstagram} />
              </a>
            )}
            {organizer?.organizer_twitter && (
              <a
                href={organizer?.organizer_twitter}
                aria-label="Follow on Twitter"
                className="hover:text-blue-500 p-2 px-3 bg-white bg-opacity-60 rounded-full transition-colors duration-300 shadow-md"
              >
                <FontAwesomeIcon icon={faTwitter} />
              </a>
            )}
            {/* Follow Button */}
            <button
              onClick={toggleFollow}
              className={`px-6 py-2 rounded-full font-semibold transition-transform duration-300 ${
                animateFollow ? "transform scale-110" : ""
              } ${
                isFollowing
                  ? "bg-primary text-white"
                  : "bg-white text-primary border border-primary hover:bg-primary hover:text-white"
              }`}
            >
              {isFollowing ? "Following" : "Follow"}
            </button>
          </div>
        </div>
      </div>

      <div className="py-10 px-4 max-w-7xl mx-auto">
        {/* Attractive Tab UI */}
        <div className="flex justify-center space-x-4 mb-8">
          {["All", "Events", "Movies"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2 rounded-full font-bold border transition-colors duration-300 ${
                activeTab === tab
                  ? "bg-primary text-white border-primary"
                  : "bg-white text-primary border-primary hover:bg-primary hover:text-white"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="flex justify-center mb-8">
          <div className="relative w-64">
            {/* Toggle background */}
            <div className="absolute inset-0 bg-gray-200 rounded-full"></div>
            {/* Sliding indicator */}
            <div
              className="absolute top-0 left-0 h-full w-1/2 bg-primary rounded-full transition-transform duration-300"
              style={{
                transform:
                  timeFilter === "Past" ? "translateX(100%)" : "translateX(0)",
              }}
            ></div>
            {/* Toggle buttons */}
            <div className="relative flex">
              <button
                onClick={() => setTimeFilter("Upcoming")}
                className={`w-1/2 py-2 text-center font-semibold transition-colors duration-300 ${
                  timeFilter === "Upcoming" ? "text-white" : "text-primary"
                }`}
              >
                Upcoming
              </button>
              <button
                onClick={() => setTimeFilter("Past")}
                className={`w-1/2 py-2 text-center font-semibold transition-colors duration-300 ${
                  timeFilter === "Past" ? "text-white" : "text-primary"
                }`}
              >
                Past
              </button>
            </div>
          </div>
        </div>

        {/* Display combined cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredList.length > 0 ? (
            filteredList.map((item) => renderCard(item))
          ) : (
            <p className="text-center col-span-full text-gray-600">
              No content available
            </p>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default ProfilePage;
